import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { getHouseUnlockPin } from "@/lib/houseLock";
import { AlertTriangle, X } from "lucide-react";

/**
 * HouseDeletionBanner — shown inside a house when another adult from
 * the family has hit "Delete" on this house from the Houses panel.
 * The people actually living in the house get the final say:
 *
 *  • Do     → actually delete (using the house PIN cached from the
 *             unlock that let them step inside in the first place).
 *  • Ignore → clear the pending request; house stays put.
 *
 * Unlocked houses never reach this banner — they get deleted
 * immediately from the panel.
 */
export default function HouseDeletionBanner({ house, onDeleted, onCancelled }) {
  const [busy, setBusy] = useState(false);

  const doDelete = async () => {
    const pin = getHouseUnlockPin(house.id);
    if (!pin) {
      toast.error("Couldn't find this house's PIN on this device. Unlock again and retry.");
      return;
    }
    if (!window.confirm(`Really delete “${house.name}”? Members will be unassigned and events un-scoped.`)) return;
    setBusy(true);
    try {
      await api.deleteHouse(house.id, { pin });
      toast.success(`“${house.name}” deleted.`);
      onDeleted && onDeleted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete");
    } finally {
      setBusy(false);
    }
  };

  const ignore = async () => {
    setBusy(true);
    try {
      await api.cancelDeleteHouse(house.id);
      toast.success("Request ignored — house stays.");
      onCancelled && onCancelled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't ignore the request");
    } finally {
      setBusy(false);
    }
  };

  const requester = house.deletion_requested_by || "Someone";

  return (
    <div
      className="rounded-xl border border-[color:var(--line)] bg-[color:var(--bg-soft)] p-4 flex flex-wrap items-start gap-3"
      data-testid={`house-deletion-banner-${house.id}`}
    >
      <div className="w-9 h-9 rounded-full bg-[color:var(--surface)] flex items-center justify-center flex-shrink-0">
        <AlertTriangle className="w-4 h-4" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="label-tiny text-[color:var(--ink-3)]">Your house is up for deleting</div>
        <h3 className="font-display-italic text-xl leading-tight mt-0.5">
          {requester} wants to delete {house.name}
        </h3>
        <p className="text-[13px] leading-snug text-[color:var(--ink-2)] mt-1">
          Deleting un-assigns everyone inside — kids land on the shared
          front for both houses — and permanently deletes any events
          scoped to this house (they won't leak into the other house).
          If you'd want to have your own space, you can always start a
          fresh FamFeel with a new family code. You have the call.
        </p>
      </div>
      <div className="flex items-center gap-2 w-full sm:w-auto">
        <button
          type="button"
          onClick={ignore}
          disabled={busy}
          className="ff-btn flex-1 sm:flex-none inline-flex items-center gap-1"
          data-testid={`house-deletion-ignore-${house.id}`}
        >
          <X className="w-3.5 h-3.5" strokeWidth={2} />
          Ignore
        </button>
        <button
          type="button"
          onClick={doDelete}
          disabled={busy}
          className="ff-btn ff-btn-primary flex-1 sm:flex-none"
          data-testid={`house-deletion-do-${house.id}`}
        >
          Do it
        </button>
      </div>
    </div>
  );
}
